/**
 * explainOp — a spec-built derived cell's `op`, said in plain English.
 *
 * compose_table(spec) records WHAT was computed as a structured `op` (name +
 * which refs it consumed) next to a generated audit string. The string is
 * exact but reads as a wall of text — "diff: mc_con_comp[1,24] (Sky x Aug-26)
 * colpc=0.19872293758047566 minus ..." — so the modal renders from the op
 * instead: one sentence, a worked calculation, and the exact stored values
 * kept in a technical line.
 *
 * Rendered from the op record, never by parsing the string: the string's
 * wording is free to change, the op's shape is schema.
 *
 * DISPLAY ROUNDING ONLY. Every figure in `lines` and `result` is rounded for
 * reading; `technical` carries the stored values and the exact result
 * untouched, because a checkable number must stay checkable.
 */
import type { CellProvenance, CellRef } from './provenance.generated'

type Unit = CellProvenance['unit']

export interface OpLine {
  /** How this input joins the ones above it ("minus", "plus"); absent on the first. */
  operator?: string
  label: string
  value: string
}

export interface OpExplanation {
  /** What this number is, in one sentence. */
  sentence: string
  /** The inputs, display-rounded, in the order the op consumed them. */
  lines: OpLine[]
  result: { label: string; value: string }
  /** Exact stored values, data indices and the op signature. Nothing rounded. */
  technical: string[]
}

const isPct = (r: CellRef) => r.metric === 'colpc' || r.metric === 'rowpc'

/**
 * An input as the op consumed it. Mirrors compose_spec's present(): a stored
 * fraction becomes a percentage only when the cell's unit is percent or pp; a
 * count, or a composite source's already-presented freq, passes through.
 */
const presented = (r: CellRef, unit: Unit) =>
  isPct(r) && (unit === 'percent' || unit === 'pp') ? r.sourceValue * 100 : r.sourceValue

const finite = (v: number, f: (v: number) => string) => (Number.isFinite(v) ? f(v) : '—')
const count = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })
const raw = (v: number) => (Math.abs(v) >= 1 || v === 0 ? v.toFixed(2) : v.toPrecision(3))
const sign = (v: number) => (v > 0 ? '+' : '')

/** A value in the cell's unit, for reading. Exported for ProvenanceModal,
 *  which uses it to format a cited cell that is itself a composite output —
 *  read by the CITING cell's own metric/unit, never the source's. */
export function inUnit(v: number, unit: Unit, signed = false): string {
  return finite(v, (n) => {
    const s = signed ? sign(n) : ''
    switch (unit) {
      case 'percent': return `${s}${n.toFixed(2)}%`
      case 'fraction': return `${s}${(n * 100).toFixed(2)}%`
      case 'pp': return `${s}${n.toFixed(2)}pp`
      case 'count': return `${s}${count(n)}`
      default: return `${s}${raw(n)}`
    }
  })
}

/** An input to a pp difference is itself a percentage. */
const inputUnit = (unit: Unit): Unit => (unit === 'pp' ? 'percent' : unit)

/** A stored value in its own metric's natural form — for ops (ratio, index,
 *  pct_change) that consume the stored value regardless of the cell's unit. */
const natural = (r: CellRef) => (isPct(r) ? inUnit(r.sourceValue, 'fraction') : finite(r.sourceValue, count))

/**
 * Name each input by what differs between them, and say once what they share:
 * Sky in Jun-26 vs Sky in Aug-26 is "Jun-26" and "Aug-26", "for Sky".
 */
function namer(refs: CellRef[]): { name: (r: CellRef) => string; shared: string } {
  const all = (f: (r: CellRef) => string) => refs.every((r) => f(r) === f(refs[0]))
  const cellName = (r: CellRef) => `${r.rowLabel} × ${r.colLabel}`
  const sameRow = all((r) => r.rowLabel)
  const sameCol = all((r) => r.colLabel)
  const sameData = all((r) => r.dataset)
  const withData = (s: string, r: CellRef) => (sameData ? s : `${s} (${r.dataset})`)
  if (sameRow && sameCol) return { name: (r) => r.dataset, shared: cellName(refs[0]) }
  if (sameRow) return { name: (r) => withData(r.colLabel, r), shared: refs[0].rowLabel }
  if (sameCol) return { name: (r) => withData(r.rowLabel, r), shared: refs[0].colLabel }
  return { name: (r) => withData(cellName(r), r), shared: '' }
}

/** "A and B", "A, B and C", or for a long run, its ends. */
function listOf(names: string[]): { text: string; long: boolean } {
  if (names.length > 5) return { text: `${names.length} values, ${names[0]} to ${names[names.length - 1]}`, long: true }
  if (names.length === 1) return { text: names[0], long: false }
  return { text: `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`, long: false }
}

const AGGREGATES: Record<string, { verb: string; label: (n: number) => string; operator?: string; f: (xs: number[]) => number }> = {
  sum: { verb: 'Total of', label: () => 'Total', operator: 'plus', f: (xs) => xs.reduce((s, x) => s + x, 0) },
  mean: { verb: 'Average of', label: (n) => `Average of ${n}`, f: (xs) => xs.reduce((s, x) => s + x, 0) / xs.length },
  min: { verb: 'Lowest of', label: () => 'Lowest', f: (xs) => Math.min(...xs) },
  max: { verb: 'Highest of', label: () => 'Highest', f: (xs) => Math.max(...xs) },
}

/**
 * @returns the explanation, or null when the cell carries no op (a hand-built
 *   derived cell, whose own description IS its explanation) or an op input is
 *   not a cell reference.
 */
export function explainOp(provenance: CellProvenance): OpExplanation | null {
  const op = provenance.op as { name: string; inputs: number[] } | undefined
  if (!op) return null
  const inputs = op.inputs.map((i) => provenance.refs[i])
  if (inputs.length === 0 || inputs.some((r) => r?.type !== 'cell')) return null
  const refs = inputs as CellRef[]
  const { unit } = provenance
  const { name, shared } = namer(refs)
  const forShared = shared ? ` for ${shared}` : ''
  const [a, b] = refs
  const A = name(a)
  const B = b ? name(b) : ''

  const technical = [
    `op: ${op.name}(${op.inputs.join(', ')}) · unit: ${unit}`,
    ...refs.map((r, k) =>
      `refs[${op.inputs[k]}]: ${r.dataset} row ${r.row}, column ${r.col} (${r.rowLabel} × ${r.colLabel}) · ${r.metric} stored ${r.sourceValue}`),
  ]
  const done = (e: Omit<OpExplanation, 'technical'>, exact: number): OpExplanation =>
    ({ ...e, technical: [...technical, `exact result: ${exact}`] })

  switch (op.name) {
    case 'value': {
      const v = presented(a, unit)
      return done({
        sentence: `Taken directly from ${a.rowLabel} × ${a.colLabel} in ${a.dataset}.`,
        lines: [{ label: `${a.rowLabel} × ${a.colLabel}`, value: inUnit(v, unit) }],
        result: { label: 'Value', value: inUnit(v, unit) },
      }, v)
    }
    case 'diff': {
      const va = presented(a, unit), vb = presented(b, unit)
      const v = va - vb
      return done({
        sentence: `Change from ${B} to ${A}${forShared}${unit === 'pp' ? ', in percentage points' : ''}.`,
        lines: [
          { label: A, value: inUnit(va, inputUnit(unit)) },
          { operator: 'minus', label: B, value: inUnit(vb, inputUnit(unit)) },
        ],
        result: { label: 'Change', value: inUnit(v, unit, true) },
      }, v)
    }
    case 'pct_change': {
      const v = ((a.sourceValue - b.sourceValue) / b.sourceValue) * 100
      return done({
        sentence: `Relative change from ${B} to ${A}${forShared}, as a percentage of ${B}.`,
        lines: [{ label: A, value: natural(a) }, { operator: 'compared with', label: B, value: natural(b) }],
        result: { label: `Relative change: (${A} − ${B}) ÷ ${B} × 100`, value: finite(v, (n) => `${sign(n)}${n.toFixed(2)}%`) },
      }, v)
    }
    case 'ratio': {
      const v = a.sourceValue / b.sourceValue
      return done({
        sentence: `${A} divided by ${B}${forShared}.`,
        lines: [{ label: A, value: natural(a) }, { operator: 'divided by', label: B, value: natural(b) }],
        result: { label: 'Ratio', value: finite(v, raw) },
      }, v)
    }
    case 'index': {
      const v = (a.sourceValue / b.sourceValue) * 100
      return done({
        sentence: `${A} as an index of ${B}${forShared}, where ${B} = 100.`,
        lines: [{ label: A, value: natural(a) }, { operator: 'indexed to', label: B, value: natural(b) }],
        result: { label: `Index (${B} = 100)`, value: finite(v, (n) => n.toFixed(1)) },
      }, v)
    }
    default: {
      const agg = AGGREGATES[op.name]
      if (!agg) return null
      const vals = refs.map((r) => presented(r, unit))
      const v = agg.f(vals)
      const list = listOf(refs.map(name))
      return done({
        sentence: `${agg.verb} ${list.text}${list.long && shared ? ',' : ''}${forShared}.`,
        lines: refs.map((r, k) => ({
          ...(k > 0 && agg.operator ? { operator: agg.operator } : {}),
          label: name(r),
          value: inUnit(vals[k], unit),
        })),
        result: { label: agg.label(refs.length), value: inUnit(v, unit) },
      }, v)
    }
  }
}
